import React, { Fragment, Component } from 'react';
/**
 * CheatSheetDrawer — Bottom sheet drawer for the Spiekbriefje (hint system).
 *
 * Slides up from the bottom with spring animation. Renders SubSkillCheatSheet
 * content with a fallback when no hints are available. Includes focus trap,
 * escape-to-close, and body scroll lock.
 *
 * Co-located in practice/ domain (CL-1 s63).
 */
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { type ModuleId, MODULES } from '../../content';
import { SubSkillCheatSheet } from './SubSkillCheatSheet';
import { useFocusTrap } from '../../app/hooks/useFocusTrap';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { useBodyScrollLock } from '../../app/hooks/useBodyScrollLock';
import { DjayAvatar, useDjaySelection, getDjayCheatsheetFallback, getDjayHelpRequestPrompt } from '../../mascot';
// ── Internal Sub-components ──────────────────────────────────
function CheatSheetContent({
  moduleId,
  subSkill,
  familyId
}: {
  moduleId: ModuleId;
  subSkill?: string;
  familyId?: string;
}) {
  return <Fragment data-id="element-6401">
      <SubSkillCheatSheet moduleId={moduleId} subSkill={subSkill} familyId={familyId} data-id="element-6402" />
      <CheatSheetFallback data-id="element-6403" />
    </Fragment>;
}
function HelpRequestContent({
  moduleId,
  subSkill,
  familyId
}: {
  moduleId: ModuleId;
  subSkill?: string;
  familyId?: string;
}) {
  const helpPrompt = getDjayHelpRequestPrompt();
  return <Fragment data-id="element-6404">
      {/* Djay's empathetic intro — unique to help-request mode */}
      <div className="bg-indigo-50 rounded-xl p-4 mb-5 border border-indigo-100" data-id="element-6405">
        <p className="text-sm font-semibold text-indigo-800 leading-relaxed" data-id="element-6406">
          {helpPrompt.text}
        </p>
        <p className="text-xs text-indigo-500 mt-1.5" data-id="element-6407">
          Bekijk de uitleg hieronder en probeer het daarna opnieuw — of sla de
          vraag over.
        </p>
      </div>
      {/* Reuse the cheat sheet hints, but framed as guided help */}
      <SubSkillCheatSheet moduleId={moduleId} subSkill={subSkill} familyId={familyId} data-id="element-6408" />
      <HelpRequestFallback data-id="element-6409" />
    </Fragment>;
}
function HelpRequestFallback() {
  return <div className="text-center py-8 text-gray-400 hidden last:block" data-id="element-6410">
      <p className="text-sm font-medium text-gray-500" data-id="element-6411">
        Djay heeft nog geen extra uitleg voor dit onderwerp. 🤔
      </p>
      <p className="text-xs text-gray-400 mt-1" data-id="element-6412">
        Probeer de vraag over te slaan en later opnieuw te bekijken.
      </p>
    </div>;
}
function CheatSheetFallback() {
  const fallback = getDjayCheatsheetFallback();
  return <div className="cheatsheet-fallback text-center py-8 text-gray-400 hidden last:block" data-id="element-6413">
      {/* @djay-slot: cheatsheet-fallback */}
      <div className="flex justify-center mb-3" data-id="element-6414">
        <DjayAvatar size="lg" emotion={fallback.emotion} data-id="element-6415" />
      </div>
      <p className="text-sm font-medium text-gray-500" data-id="element-6416">{fallback.text}</p>
      <p className="text-xs text-gray-400 mt-1" data-id="element-6417">
        Voor deze vaardigheid is nog geen spiekbriefje gemaakt. Probeer de
        hint-knop tijdens het oefenen.
      </p>
    </div>;
}
// ── Types ────────────────────────────────────────────────────
interface CheatSheetDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  moduleId: ModuleId;
  subSkill?: string;
  familyId?: string;
  mode?: 'cheatsheet' | 'help-request';
  onSkip?: () => void;
}
// ── Component ────────────────────────────────────────────────
export function CheatSheetDrawer({
  isOpen,
  onClose,
  moduleId,
  subSkill,
  familyId,
  mode = 'cheatsheet',
  onSkip
}: CheatSheetDrawerProps) {
  const focusTrapRef = useFocusTrap(isOpen);
  const {
    selectedVariant
  } = useDjaySelection();
  useEscapeKey(onClose, isOpen);
  useBodyScrollLock(isOpen);
  return <AnimatePresence data-id="element-6418">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40" data-id="element-6419" />

          {/* Bottom Sheet */}
          <motion.div ref={focusTrapRef} role="dialog" aria-modal="true" aria-labelledby="cheatsheet-title" initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 32,
        stiffness: 350
      }} className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl max-h-[75vh] flex flex-col" data-id="element-6420">
            {/* Drag Handle */}
            <div className="flex justify-center pt-3 pb-1" data-id="element-6421">
              <div className="w-10 h-1 rounded-full bg-slate-300" data-id="element-6422" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 pb-3 border-b border-slate-100" data-id="element-6423">
              <div className="flex items-center gap-2.5" data-id="element-6424">
                <DjayAvatar size="md" emotion={mode === 'help-request' ? 'encouraging' : 'pointing'} variant={selectedVariant} data-id="element-6425" />
                <div data-id="element-6426">
                  <h2 id="cheatsheet-title" className="text-lg font-bold text-slate-900 leading-tight" data-id="element-6427">
                    {mode === 'help-request' ? 'Djay helpt je 🤝' : 'Spiekbriefje'}
                  </h2>
                  <p className="text-[11px] text-slate-400 font-medium uppercase tracking-wider" data-id="element-6428">
                    {MODULES.find(m => m.id === moduleId)?.title ?? moduleId}
                    {subSkill ? ` · ${subSkill}` : ''}
                  </p>
                </div>
              </div>

              <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-600" aria-label="Sluiten" data-id="element-6429">
                <X className="w-5 h-5" data-id="element-6430" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4" data-id="element-6431">
              {mode === 'help-request' ? <HelpRequestContent moduleId={moduleId} subSkill={subSkill} familyId={familyId} data-id="element-6432" /> : <CheatSheetContent moduleId={moduleId} subSkill={subSkill} familyId={familyId} data-id="element-6433" />}
            </div>

            {/* Skip Option for Help Request */}
            {mode === 'help-request' && onSkip && <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-center" data-id="element-6434">
                <button onClick={onSkip} className="text-sm font-bold text-slate-500 hover:text-slate-700 transition-colors flex items-center gap-2" data-id="element-6435">
                  Sla deze vraag over
                  <span aria-hidden="true" data-id="element-6436">→</span>
                </button>
              </div>}
          </motion.div>
        </>}
    </AnimatePresence>;
}